import { useMemo, useState } from 'react';
import { useBlockDialogEscape } from '@ais-ui/ui-core';
import { AutoCompleteFilterTextarea as FilterQueryTextarea } from '@ais-ui/filter-query';
import { useLocalization } from '@ais-ui/locale-management';
import { useShapesList } from '@ais-ui/api';
import { useFilterContext } from '../../Map/FilterContext';
import { filterAndFlattenShapes } from '../../Shapes/hooks';

interface AutoCompleteFilterTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * MDM wiring for the shared filter autocomplete: supplies the map's filter
 * vocabulary, the shapes available for position fields, and localized strings.
 */
export const AutoCompleteFilterTextarea: React.FC<
  AutoCompleteFilterTextareaProps
> = ({ id, value, onChange, placeholder }) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();

  // MDM renders this inside a DialogForm; keep Escape from closing the dialog
  // while the suggestion dropdown is open. AISAdmin renders it on a page, so
  // this dialog-only concern lives here rather than in the shared component.
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  useBlockDialogEscape(autocompleteOpen);

  const { data: rawShapeData } = useShapesList();
  const areaValues = useMemo(() => {
    const shapes = rawShapeData?.records ? rawShapeData.records : [];
    return filterAndFlattenShapes(shapes, '');
  }, [rawShapeData]);

  return (
    <FilterQueryTextarea
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      filterOptions={filterOptions}
      areaValues={areaValues}
      messages={messages}
      onAutocompleteOpenChange={setAutocompleteOpen}
    />
  );
};
